import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecording } from '../contexts/RecordingContext';
import useIsMobile from '../hooks/useIsMobile';
import useVisualViewport from '../hooks/useVisualViewport';
import { boundsOf, clampToBounds, defaultPos, scaleOf } from '../lib/floatingBounds';
import { TABBAR_H } from '../lib/tabbarMetrics';

const STORAGE_KEY = 'recFloatPos';
const MARGIN = 8;
const TAB_BAR_SELECTOR = 'nav[aria-label="모바일 주요 메뉴"]';

// 알약 위치를 스타일 px 로 잰다. 모바일은 <html> zoom 때문에 rect(화면 px)와 left/top(스타일 px)이
// zoom 배만큼 다르고, 하단 탭바(position:fixed)가 아래를 덮는다 — 둘 다 반영한 영역(bounds)을 돌려준다.
function measure(node, { mobile, keyboardOpen }) {
  const rect = node.getBoundingClientRect();
  // 배율은 높이로 잰다 — offsetWidth 는 정수로 반올림돼 글자 폭이 소수인 알약에서 데스크톱도 1 이 안 나오고
  // (예: 186.5/187 = 0.997) 끌 때마다 몇 px 씩 튄다. 알약 높이는 36 고정이라 offsetHeight 가 정확하다.
  const scale = scaleOf(rect.height, node.offsetHeight);
  const navRect = Array.from(document.querySelectorAll(TAB_BAR_SELECTOR))
    .map((nav) => nav.getBoundingClientRect())
    .find((r) => r.height > 0);
  let navTop = navRect ? navRect.top : null;
  if (navTop == null && mobile && !keyboardOpen) navTop = window.innerHeight - TABBAR_H * scale;
  const bounds = boundsOf({ innerWidth: window.innerWidth, innerHeight: window.innerHeight, scale, navTop });
  return {
    scale,
    bounds,
    left: rect.left / scale,
    top: rect.top / scale,
    width: rect.width / scale,
    height: rect.height / scale,
  };
}

function formatElapsed(sec) {
  const safe = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function readSavedPos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {}
  return null;
}

export default function FloatingRecorder() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    recording,
    recFinalizing,
    recPreview,
    recPaused,
    recPauseSupported,
    recordingSec,
    pauseRec,
    resumeRec,
    stopRec,
    floatingError,
    pendingUploads = [],
    retryPendingUpload,
    recordingScreenMinimized,
    restoreRecordingScreen,
    recoverNavSeq,
    silenceWarnSec,
    meterFailed,
    noSoundWarn,
    keepRecording,
  } = useRecording();
  const uploadItems = Array.isArray(pendingUploads) ? pendingUploads : [];
  const uploadingUploads = uploadItems.filter((item) => item.status === 'uploading');
  const failedUploads = uploadItems.filter((item) => item.status === 'failed');
  const latestUploading = uploadingUploads[uploadingUploads.length - 1];
  const firstFailed = failedUploads[0];
  const hasPendingUploads = uploadItems.length > 0;
  const onMeetings = location.pathname.startsWith('/meetings');
  const visible = (recording || recFinalizing || !!recPreview || hasPendingUploads || !!floatingError) && (!onMeetings || recordingScreenMinimized);
  const ref = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [pos, setPos] = useState(readSavedPos);
  const mobile = useIsMobile();
  const { keyboardOpen } = useVisualViewport();

  // 녹음 화면(알약이 안 보이는 곳)에서도 «녹음 중»을 <html> 에 표시한다 —
  // PushSetupBanner 가 이 표식으로 스스로 숨어 녹음 화면 상태바 버튼을 덮지 않는다.
  useEffect(() => {
    const root = document.documentElement;
    const active = recording || recFinalizing || !!recPreview;
    if (active) root.dataset.gwRecording = '1';
    else delete root.dataset.gwRecording;
    return () => { delete root.dataset.gwRecording; };
  }, [recording, recFinalizing, recPreview]);

  const clampPos = useCallback((x, y, width, height, bounds) => (
    clampToBounds(x, y, width, height, bounds, MARGIN)
  ), []);

  // 지금 자리(없으면 기본 자리)를 잰 영역 안으로 당기고 저장한다.
  const reclamp = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    const m = measure(node, { mobile, keyboardOpen });
    setPos((current) => {
      const base = current || defaultPos(m.width, m.height, m.bounds, 16);
      const next = clampPos(base.x, base.y, m.width, m.height, m.bounds);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [clampPos, mobile, keyboardOpen]);

  // 알약은 좌표(left)로 고정되므로 내용이 길어지면 오른쪽으로 자란다 —
  // 무음 경고나 업로드 표시가 붙는 순간 화면 밖으로 밀려 버튼을 못 누른다.
  // 그래서 내용 구성이 바뀔 때마다 다시 화면 안으로 당긴다.
  const contentKey = `${recording}|${recFinalizing}|${!!recPreview}|${silenceWarnSec != null}|${meterFailed}|${noSoundWarn}|${hasPendingUploads}|${!!floatingError}`;

  // 화면이 바뀌거나 키보드가 닫혀 탭바가 다시 나타나면 다시 잰다(effect 는 커밋 뒤라 탭바 DOM 이 새 상태다).
  // 메신저·드라이브 같은 임베드 화면은 탭바가 이 effect 보다 뒤 커밋에서 나타나 다음 프레임에 한 번 더 잰다.
  useEffect(() => {
    if (!visible) return undefined;
    reclamp();
    const frame = window.requestAnimationFrame(reclamp);
    return () => window.cancelAnimationFrame(frame);
  }, [reclamp, visible, contentKey, location.pathname, keyboardOpen, mobile]);

  useEffect(() => {
    if (!visible) return undefined;
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [reclamp, visible]);

  // 휴대폰 회전·글자 크기(+/−)로 MobileScale 이 <html> zoom 을 바꾸면 다시 잰다 — 회전의 resize 는 zoom 이
  // 바뀌기 전(옛 배율)에 오고, 글자 크기 버튼은 resize 없이 zoom 만 바꿔 옛 배율로 잰 자리가 화면 밖·탭바 아래에 남는다.
  // zoom 이 실제로 바뀐 때만 잰다 — MobileScale 은 주소창·키보드마다 --gw-app-h 등을 다시 써서(같은 값이어도
  // mutation 이 생긴다) 매번 재면 녹음 중 프레임마다 강제 레이아웃·localStorage 쓰기가 돈다.
  useEffect(() => {
    if (!visible) return undefined;
    if (typeof MutationObserver !== 'function') return undefined;
    const root = document.documentElement;
    let lastZoom = root.style.zoom;
    const observer = new MutationObserver(() => {
      // 끄는 중에는 손가락 아래에서 튀지 않게 건너뛴다 — 기억값도 그대로 둬 놓은 뒤 다음 style 변경 때 반영된다.
      if (dragRef.current) return;
      const zoom = root.style.zoom;
      if (zoom === lastZoom) return;
      lastZoom = zoom;
      reclamp();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [reclamp, visible]);

  const handlePointerDown = (e) => {
    if (e.target?.closest?.('button, a, input')) return;
    if (!visible || e.button !== 0) return;
    const node = ref.current;
    if (!node) return;
    const m = measure(node, { mobile, keyboardOpen });
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseX: m.left,
      baseY: m.top,
      width: m.width,
      height: m.height,
      scale: m.scale,
      bounds: m.bounds,
      moved: false,
    };
    node.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const screenDx = e.clientX - drag.startX;
    const screenDy = e.clientY - drag.startY;
    if (Math.abs(screenDx) > 5 || Math.abs(screenDy) > 5) drag.moved = true;
    const dx = screenDx / drag.scale;
    const dy = screenDy / drag.scale;
    const next = clampPos(drag.baseX + dx, drag.baseY + dy, drag.width, drag.height, drag.bounds);
    setPos(next);
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    ref.current?.releasePointerCapture?.(e.pointerId);
    if (drag.moved) {
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
      const node = ref.current;
      if (node) {
        const m = measure(node, { mobile, keyboardOpen });
        const saved = { x: m.left, y: m.top };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(saved)); } catch {}
      }
    }
  };

  const handleClickCapture = (e) => {
    if (!suppressClickRef.current) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRestoreClick = (e) => {
    if (suppressClickRef.current) return;
    if (e.target?.closest?.('button, a, input')) return;
    restoreRecordingScreen?.();
    if (!onMeetings) navigate('/meetings');
  };

  // 복구(주인 없는 녹음)를 고르면 그 미리보기가 있는 AI 노트로 바로 간다 — 띠를 한 번 더 누르지 않게(#1869).
  // 마운트 때 이미 있던 값은 «새 복구»가 아니다 → 처음 값을 기억해 두고 바뀔 때만 움직인다.
  // 🔴 아래 `if (!visible) return null;` 보다 위에 둔다 — AI 노트 화면에서는 띠가 숨어도(visible=false) 펼쳐야 한다.
  const recoverNavSeenRef = useRef(recoverNavSeq || 0);
  useEffect(() => {
    const seq = recoverNavSeq || 0;
    if (seq === recoverNavSeenRef.current) return;
    recoverNavSeenRef.current = seq;
    restoreRecordingScreen?.();
    if (!location.pathname.startsWith('/meetings')) navigate('/meetings');
  }, [recoverNavSeq]);

  if (!visible) return null;

  const busy = recFinalizing;
  const dotColor = recPaused ? '#94a3b8' : '#ef4444';
  const recordingControls = (recording || recFinalizing) ? (
    <>
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dotColor,
          animation: recPaused ? 'none' : 'rec-float-pulse 1.1s ease-in-out infinite',
          flexShrink: 0,
        }}
      />
      <span style={{ minWidth: 44, fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {formatElapsed(recordingSec)}
      </span>
      {silenceWarnSec == null && meterFailed && (
        <span
          title="마이크 레벨을 읽지 못해 무음 자동 중지가 동작하지 않습니다. 녹음은 계속됩니다 — 소리가 들어오는지 직접 확인해 주세요."
          style={{ fontSize: 12, fontWeight: 800, color: '#f87171', whiteSpace: 'nowrap' }}
        >
          ⚠ 자동중지 꺼짐
        </span>
      )}
      {silenceWarnSec == null && !meterFailed && noSoundWarn && (
        <span
          title="녹음이 시작된 뒤 지금까지 소리가 감지되지 않았습니다. 마이크 연결·권한·입력 장치를 확인해 주세요."
          style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24', whiteSpace: 'nowrap' }}
        >
          ⚠ 소리 안 들어옴
        </span>
      )}
      {silenceWarnSec != null && (
        <>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#fbbf24', whiteSpace: 'nowrap' }}>
            소리 없음 · {silenceWarnSec}초 후 중지
          </span>
          <button
            type="button"
            onClick={keepRecording}
            title="녹음을 계속합니다"
            style={{
              height: 22, borderRadius: 999, border: '1px solid rgba(255,255,255,.35)',
              background: 'transparent', color: '#fff', cursor: 'pointer',
              padding: '0 9px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
            }}
          >
            계속
          </button>
        </>
      )}
      <button
        type="button"
        onClick={recPaused ? resumeRec : pauseRec}
        disabled={busy || !recPauseSupported}
        title={recPaused ? '녹음 재개' : '녹음 일시정지'}
        aria-label={recPaused ? '녹음 재개' : '녹음 일시정지'}
        style={{
          width: 24,
          height: 24,
          border: 'none',
          borderRadius: '50%',
          background: 'rgba(255,255,255,.11)',
          color: '#fff',
          cursor: busy || !recPauseSupported ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          opacity: busy || !recPauseSupported ? 0.45 : 1,
          fontSize: 12,
          lineHeight: 1,
        }}
      >
        {recPaused ? '▶' : '⏸'}
      </button>
      <button
        type="button"
        onClick={() => stopRec({ autoUpload: true })}
        disabled={busy}
        title="녹음 중지"
        aria-label="녹음 중지"
        style={{
          width: 24,
          height: 24,
          border: 'none',
          borderRadius: '50%',
          background: 'rgba(255,255,255,.11)',
          color: '#fff',
          cursor: busy ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          opacity: busy ? 0.65 : 1,
          fontSize: 12,
          lineHeight: 1,
        }}
      >
        {busy ? <span aria-hidden="true" style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'rec-float-spin .8s linear infinite' }} /> : '⏹'}
      </button>
    </>
  ) : null;

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClickCapture={handleClickCapture}
      onClick={handleRestoreClick}
      title="클릭 시 녹음 화면으로 돌아갑니다"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: pos ? `${pos.x}px` : 'auto',
        top: pos ? `${pos.y}px` : 'auto',
        right: pos ? 'auto' : 16,
        bottom: pos ? 'auto' : 16,
        zIndex: 2147483000,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        borderRadius: 999,
        background: 'rgba(17,24,39,.88)',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(15,23,42,.24)',
        padding: '0 10px',
        fontFamily: 'var(--gw-font)',
        cursor: 'grab',
        userSelect: 'none',
        touchAction: 'none',
        backdropFilter: 'blur(6px)',
      }}
    >
      <style>{`@keyframes rec-float-pulse{0%,100%{opacity:.45}50%{opacity:1}}@keyframes rec-float-spin{to{transform:rotate(360deg)}}`}</style>
      {hasPendingUploads ? (
        <>
          {uploadingUploads.length > 0 && (
            <span style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>
              ⤴ 업로드 중 {uploadingUploads.length > 1 ? `${uploadingUploads.length}건 · ` : ''}{latestUploading?.pct ?? 0}%
            </span>
          )}
          {failedUploads.length > 0 && (
            <>
              <span style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', color: '#fecaca' }}>
                ⚠ {floatingError || `업로드 실패 ${failedUploads.length}건`}
              </span>
              <button
                type="button"
                onClick={() => firstFailed && retryPendingUpload?.(firstFailed.id)}
                title="실패한 녹음 업로드 재시도"
                aria-label="실패한 녹음 업로드 재시도"
                style={{
                  minWidth: 46,
                  height: 24,
                  border: 'none',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,.14)',
                  color: '#fff',
                  cursor: firstFailed ? 'pointer' : 'not-allowed',
                  padding: '0 9px',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                재시도
              </button>
            </>
          )}
          {recordingControls}
        </>
      ) : recPreview ? (
        <span style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>미리듣기 확인</span>
      ) : floatingError ? (
        <span style={{ fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>{floatingError}</span>
      ) : (
        recordingControls
      )}
    </div>
  );
}
